// Generic spotlight tour engine.
// Extracted from ReportTour so it can be reused for sidebar, report, etc.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { TourStep } from './tourSteps'

interface Rect {
  top: number
  left: number
  width: number
  height: number
}

const PADDING = 8
const TOOLTIP_GAP = 14
const TOOLTIP_W = 300
const TOOLTIP_MARGIN = 12
const OVERLAY_Z = 9990
const TOOLTIP_Z = 9991

function getTargetRect(step: TourStep): Rect | null {
  const el = document.querySelector(`[data-tour="${step.target}"]`)
  if (!el) return null
  const r = el.getBoundingClientRect()
  return { top: r.top, left: r.left, width: r.width, height: r.height }
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function computeTooltipStyle(rect: Rect, preferido: TourStep['posicion']): React.CSSProperties {
  const vw = document.documentElement.clientWidth
  const vh = document.documentElement.clientHeight
  const style: React.CSSProperties = { position: 'fixed', zIndex: TOOLTIP_Z, width: TOOLTIP_W }

  const spotLeft = rect.left - PADDING
  const spotRight = rect.left + rect.width + PADDING
  const spotTop = rect.top - PADDING
  const spotBottom = rect.top + rect.height + PADDING
  const centerY = rect.top + rect.height / 2
  const centerX = rect.left + rect.width / 2

  const spaceRight = vw - spotRight - TOOLTIP_GAP
  const spaceLeft = spotLeft - TOOLTIP_GAP
  const spaceBottom = vh - spotBottom - TOOLTIP_GAP
  const spaceTop = spotTop - TOOLTIP_GAP

  let side = preferido
  if (side === 'right' && spaceRight < TOOLTIP_W + TOOLTIP_MARGIN) {
    side = spaceLeft >= TOOLTIP_W + TOOLTIP_MARGIN ? 'left' : 'bottom'
  } else if (side === 'left' && spaceLeft < TOOLTIP_W + TOOLTIP_MARGIN) {
    side = spaceRight >= TOOLTIP_W + TOOLTIP_MARGIN ? 'right' : 'bottom'
  } else if (side === 'bottom' && spaceBottom < 120) {
    side = spaceTop >= 120 ? 'top' : 'right'
  } else if (side === 'top' && spaceTop < 120) {
    side = spaceBottom >= 120 ? 'bottom' : 'right'
  }

  switch (side) {
    case 'right':
      style.left = spotRight + TOOLTIP_GAP
      style.top = clamp(centerY - 80, TOOLTIP_MARGIN, vh - 200)
      break
    case 'left':
      style.left = clamp(spotLeft - TOOLTIP_GAP - TOOLTIP_W, TOOLTIP_MARGIN, vw - TOOLTIP_W - TOOLTIP_MARGIN)
      style.top = clamp(centerY - 80, TOOLTIP_MARGIN, vh - 200)
      break
    case 'bottom':
      style.top = spotBottom + TOOLTIP_GAP
      style.left = clamp(centerX - TOOLTIP_W / 2, TOOLTIP_MARGIN, vw - TOOLTIP_W - TOOLTIP_MARGIN)
      break
    case 'top':
      style.top = clamp(spotTop - TOOLTIP_GAP - 200, TOOLTIP_MARGIN, vh - 200)
      style.left = clamp(centerX - TOOLTIP_W / 2, TOOLTIP_MARGIN, vw - TOOLTIP_W - TOOLTIP_MARGIN)
      break
  }

  return style
}

interface SpotlightTourProps {
  steps: TourStep[]
  onClose: () => void
}

export function SpotlightTour({ steps, onClose }: SpotlightTourProps) {
  const [step, setStep] = useState(0)
  const [rect, setRect] = useState<Rect | null>(null)
  const [visible, setVisible] = useState(false)
  const rafRef = useRef(0)

  const currentStep = steps[step]

  const measureTarget = useCallback(() => {
    if (!currentStep) return
    const r = getTargetRect(currentStep)
    setRect(r)
  }, [currentStep])

  const finish = useCallback(() => {
    onClose()
  }, [onClose])

  // Auto-skip steps whose target element doesn't exist in the DOM
  useEffect(() => {
    if (!currentStep) return
    const r = getTargetRect(currentStep)
    if (!r && step < steps.length - 1) {
      setStep(s => s + 1)
    } else if (!r && step === steps.length - 1) {
      finish()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  useLayoutEffect(() => {
    measureTarget()
  }, [measureTarget])

  useEffect(() => {
    const onResize = () => {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(measureTarget)
    }
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', onResize, true)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onResize, true)
      cancelAnimationFrame(rafRef.current)
    }
  }, [measureTarget])

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 50)
    return () => clearTimeout(t)
  }, [])

  const next = useCallback(() => {
    if (step < steps.length - 1) {
      setStep(s => s + 1)
    } else {
      finish()
    }
  }, [step, steps.length, finish])

  const prev = useCallback(() => {
    if (step > 0) setStep(s => s - 1)
  }, [step])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { finish(); return }
      if (e.key === 'ArrowRight' || e.key === 'Enter') { next(); return }
      if (e.key === 'ArrowLeft') { prev(); return }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [next, prev, finish])

  if (!currentStep) return null

  const cutout = rect
    ? {
        top: rect.top - PADDING,
        left: rect.left - PADDING,
        width: rect.width + PADDING * 2,
        height: rect.height + PADDING * 2,
      }
    : null

  return (
    <div
      className={cn(
        'transition-opacity duration-300',
        visible ? 'opacity-100' : 'opacity-0',
      )}
    >
      {cutout ? (
        <div
          style={{
            position: 'fixed',
            top: cutout.top,
            left: cutout.left,
            width: cutout.width,
            height: cutout.height,
            borderRadius: 10,
            zIndex: OVERLAY_Z,
            pointerEvents: 'none',
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.75)',
          }}
        />
      ) : (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: OVERLAY_Z,
            pointerEvents: 'none',
            background: 'rgba(0,0,0,0.75)',
          }}
        />
      )}

      <div
        style={{ position: 'fixed', inset: 0, zIndex: OVERLAY_Z, cursor: 'pointer' }}
        onClick={finish}
      />

      {cutout && (
        <div
          style={{
            position: 'fixed',
            top: cutout.top,
            left: cutout.left,
            width: cutout.width,
            height: cutout.height,
            zIndex: OVERLAY_Z + 1,
            pointerEvents: 'none',
          }}
          className="rounded-[10px] ring-2 ring-teal-400/60 shadow-[0_0_30px_rgba(45,212,191,0.25)]"
        />
      )}

      {rect && (
        <div
          style={computeTooltipStyle(rect, currentStep.posicion)}
          className="relative rounded-xl border border-white/[0.1] bg-[#0f1629] p-4 shadow-2xl shadow-black/50"
          onClick={e => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={finish}
            aria-label="Cerrar tour"
            className="absolute right-2.5 top-2.5 z-10 rounded-md p-1 text-white/25 transition-colors hover:bg-white/[0.06] hover:text-white/60"
          >
            <X className="w-3.5 h-3.5" />
          </button>

          <div className="mb-2.5 flex items-center gap-1.5 pr-8">
            {steps.map((_, i) => (
              <div
                key={i}
                className={cn(
                  'h-1 rounded-full transition-all duration-300',
                  i === step
                    ? 'w-5 bg-teal-400'
                    : i < step
                    ? 'w-2 bg-teal-400/40'
                    : 'w-2 bg-white/10',
                )}
              />
            ))}
            <span className="ml-auto text-[10px] text-white/25">
              {step + 1}/{steps.length}
            </span>
          </div>

          <h3 className="text-[13px] font-semibold text-white/90 mb-1.5">
            {currentStep.titulo}
          </h3>
          <p className="text-[12px] leading-relaxed text-white/55 mb-4">
            {currentStep.descripcion}
          </p>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={finish}
              className="text-[11px] text-white/30 hover:text-white/60 transition-colors px-2 py-1.5"
            >
              Omitir tour
            </button>

            <div className="flex-1" />

            {step > 0 && (
              <button
                type="button"
                onClick={prev}
                className="flex items-center gap-1 text-[11px] text-white/45 hover:text-white/75 px-2.5 py-1.5 rounded-md hover:bg-white/[0.06] transition-colors"
              >
                <ChevronLeft className="w-3 h-3" /> Anterior
              </button>
            )}

            <button
              type="button"
              onClick={next}
              className="flex items-center gap-1 text-[11px] font-medium text-slate-900 px-3 py-1.5 rounded-md bg-teal-400 hover:bg-teal-300 transition-colors"
            >
              {step < steps.length - 1 ? (
                <>Siguiente <ChevronRight className="w-3 h-3" /></>
              ) : (
                'Empezar'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
